import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";
import {
  Transaction,
  PaginationInfo,
  FilterParams,
} from "./constants/dataTypes";
import TransactionSummary from "./components/TransactionSummary";
import TransactionFilters from "./components/TransactionFilters";
import TransactionsTable from "./components/TransactionsTable";
import Pagination from "./components/Pagination";
const Transactions = () => {
  const AppService = useAppServices();
  const { user } = useUserInfo();

  // State management
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  // Filter states
  const [filters, setFilters] = useState({
    searchTerm: "",
    statusFilter: "all",
    entityTypeFilter: "all",
    dateRange: {
      startDate: "",
      endDate: "",
    },
  });

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce effect for search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.searchTerm]);

  // Fetch transactions
  const fetchTransactions = async (params: FilterParams = {}) => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams();

      // Add company filter
      if (user?.companyId) {
        queryParams.append("refId", user.companyId);
        queryParams.append("refType", "company");
      }

      // Add pagination
      queryParams.append(
        "page",
        params.page?.toString() || pagination.page.toString()
      );
      queryParams.append(
        "limit",
        params.limit?.toString() || pagination.limit.toString()
      );

      // Add filters
      if (debouncedSearch) queryParams.append("search", debouncedSearch);
      if (filters.statusFilter !== "all")
        queryParams.append("status", filters.statusFilter);
      if (filters.entityTypeFilter !== "all")
        queryParams.append("entityType", filters.entityTypeFilter);
      if (filters.dateRange.startDate)
        queryParams.append("startDate", filters.dateRange.startDate);
      if (filters.dateRange.endDate)
        queryParams.append("endDate", filters.dateRange.endDate);

      const { response } = await AppService.transaction.filter({
        query: queryParams.toString(),
      });

      if (response?.success) {
        setTransactions(response.data);
        setPagination(response.pagination);
      } else {
        setTransactions([]);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load and when filters change
  let pageLoad = true;
  useEffect(() => {
    if (pageLoad) {
      fetchTransactions({ page: 1 });
      pageLoad = false;
    }
  }, [
    debouncedSearch,
    filters.statusFilter,
    filters.entityTypeFilter,
    filters.dateRange,
  ]);

  // Handlers
  const handleFilterChange = (newFilters: any) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleClearFilters = () => {
    setFilters({
      searchTerm: "",
      statusFilter: "all",
      entityTypeFilter: "all",
      dateRange: { startDate: "", endDate: "" },
    });
  };

  const handlePageChange = (newPage: number) => {
    fetchTransactions({ page: newPage });
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination((prev) => ({ ...prev, limit: newLimit }));
    fetchTransactions({ page: 1, limit: newLimit });
  };

  const handleViewDetails = (transaction: Transaction) => {
    // Implement view details logic
    console.log("View transaction:", transaction);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Transaction History
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {pagination.total} transactions found
          </p>
        </div>
        <button className="flex items-center justify-center space-x-2 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-md dark:hover:shadow-gray-900/20 w-full lg:w-auto">
          <Download size={18} />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Summary Stats - CORRECT USAGE */}
      <TransactionSummary transactions={transactions} pagination={pagination} />

      {/* Filters - CORRECT USAGE */}
      <TransactionFilters
        filters={filters}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onLimitChange={handleLimitChange}
        onClearFilters={handleClearFilters}
      />

      {/* Transactions Table */}
      <TransactionsTable
        transactions={transactions}
        loading={loading}
        onViewDetails={handleViewDetails}
      />

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
      )}
    </div>
  );
};

export default Transactions;
